'use client';

import { createContext, useContext, useEffect, useState, useRef, useMemo } from 'react';
import { getBrowserClient, clearLocalAuthSession } from '@/lib/supabase/client';
import { clearSessionAppState } from '@/lib/auth/clear-session-state';
import { unsubscribeCurrentDevicePush } from '@/lib/push/logout-cleanup';
import {
  isInvalidSessionError,
  isLockConflictError,
  isRateLimitError,
  isRefreshTokenInvalidError,
  isUserNotFoundError,
} from '@/lib/supabase/auth-errors';
import { normalizeEmail } from '@/lib/auth/normalize-email';
import { syncProfileNameFromUser } from '@/lib/auth/sync-profile-name';
import { mapLoginError } from '@/lib/auth/login-errors';
import { assertNewSignUpUser, mapSignupError } from '@/lib/auth/signup-validation';
import { withTimeout } from '@/lib/auth/timeout';
import {
  buildPresentationDemoProfile,
  buildPresentationDemoUser,
  isPresentationDemoFeatureEnabled,
  isPresentationDemoUserId,
} from '@/lib/presentation-demo';
import type { BillingIdentityLink } from '@/lib/billing/billing-identity';
import {
  fetchBillingIdentityViaApi,
  loadBillingIdentityForUser,
} from '@/lib/billing/load-billing-identity';
import {
  logPlanResolution,
  resolvePlanTier,
  resolvePlanTierFromBillingIdentity,
  subscriptionStatusFromBilling,
} from '@/lib/billing/resolve-plan';
import { resolveEntitlement } from '@/lib/billing/entitlement';
import type { PlanTier } from '@/lib/planLimits';
import { isAccountLifecycleState, type AccountLifecycleState } from '@/lib/account-lifecycle';

interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  avatar_url: string;
  role: 'individual' | 'institution';
  institution_name: string;
  institution_id: string;
  vault_id: string;
  public_handle?: string | null;
  is_active: boolean;
  account_lifecycle_state?: AccountLifecycleState;
  deletion_scheduled_for?: string | null;
  created_at: string;
  subscription_tier?: string;
  stripe_customer_id?: string;
  stripe_subscription_id?: string;
  stripe_email?: string;
  login_email?: string;
  subscription_status?: string;
  price_id?: string;
  subscription_current_period_end?: string;
  plan?: string;
  profile_setup_complete?: boolean;
  location?: string;
  birth_year?: number;
  occupation?: string;
  bio?: string;
  storage_used_bytes?: number;
}

export type { BillingIdentityLink };

function buildSyntheticProfile(
  authUser: {
    id: string;
    email?: string | null;
    created_at?: string;
    user_metadata?: Record<string, unknown>;
  },
  billing: BillingIdentityLink | null
): UserProfile {
  const meta = authUser.user_metadata || {};
  const role = meta.role === 'institution' ? 'institution' : 'individual';
  const syntheticFields = {
    subscription_tier: billing?.plan || 'free',
    subscription_status: billing?.status || 'free',
    stripe_customer_id: billing?.stripe_customer_id || '',
    stripe_subscription_id: billing?.stripe_subscription_id || '',
    price_id: billing?.price_id || '',
    role,
  };
  const entitlement = resolveEntitlement(syntheticFields, billing);
  const tier = entitlement.tier;
  const email = authUser.email || billing?.login_email || '';
  return {
    id: authUser.id,
    email,
    full_name: String(meta.full_name || meta.fullName || email.split('@')[0] || 'Member'),
    avatar_url: String(meta.avatar_url || ''),
    role,
    institution_name: String(meta.institution_name || ''),
    institution_id: String(meta.institution_id || ''),
    vault_id: String(
      meta.vault_id ||
        `${role === 'institution' ? 'INST' : 'ERV'}-${authUser.id.replace(/-/g, '').slice(0, 8).toUpperCase()}`
    ),
    public_handle: null,
    is_active: true,
    account_lifecycle_state: 'active',
    deletion_scheduled_for: null,
    created_at: authUser.created_at || new Date().toISOString(),
    subscription_tier: tier,
    stripe_customer_id: billing?.stripe_customer_id || '',
    stripe_subscription_id: billing?.stripe_subscription_id || '',
    stripe_email: billing?.stripe_email || '',
    login_email: billing?.login_email || email,
    subscription_status: billing?.status || 'free',
    price_id: billing?.price_id || '',
    profile_setup_complete: true,
    location: '',
    occupation: '',
    bio: '',
    storage_used_bytes: 0,
  };
}

type LifecycleProjection = {
  state?: unknown;
  deletion_scheduled_for?: string | null;
};

function attachLifecycleToProfile(
  profile: UserProfile,
  lifecycle: LifecycleProjection | null | undefined
): UserProfile {
  const state = isAccountLifecycleState(lifecycle?.state)
    ? lifecycle.state
    : profile.is_active === false
      ? 'deactivated'
      : 'active';

  return {
    ...profile,
    account_lifecycle_state: state,
    deletion_scheduled_for: lifecycle?.deletion_scheduled_for ?? null,
  };
}

/**
 * Whether `profile` reflects a confirmed, current resolution -- not just whether it's non-null.
 * `buildSyntheticProfile()` never sets `onboarding_complete`, and a real query failure can leave
 * `profile` non-null but wrong (see fetchProfile below): consumers that gate a decision on
 * onboarding completion must check `profileStatus === 'ready'`, not `profile !== null`.
 */
export type ProfileStatus = 'idle' | 'loading' | 'ready' | 'error';

interface AuthContextType {
  user: any;
  session: any;
  profile: UserProfile | null;
  profileStatus: ProfileStatus;
  billingIdentity: BillingIdentityLink | null;
  resolvedPlan: PlanTier;
  resolvedSubscriptionStatus: string;
  loading: boolean;
  isPresentationDemo: boolean;
  signUp: (email: string, password: string, metadata?: Record<string, any>) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: (options?: { redirectToLogin?: boolean; sessionMessage?: string }) => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  getUserProfile: (userId?: string) => Promise<UserProfile | null>;
  refreshProfile: () => Promise<void>;
  sessionInvalidMessage: string | null;
  clearSessionInvalidMessage: () => void;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profileStatus, setProfileStatus] = useState<ProfileStatus>('idle');
  const [billingIdentity, setBillingIdentity] = useState<BillingIdentityLink | null>(null);
  const [loading, setLoading] = useState(true);
  const [isPresentationDemo, setIsPresentationDemo] = useState(false);
  const [sessionInvalidMessage, setSessionInvalidMessage] = useState<string | null>(null);
  const supabase = getBrowserClient();
  const profileFetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const presentationDemoActiveRef = useRef(false);
  const invalidatingRef = useRef(false);
  const validatingRef = useRef(false);

  const endInvalidSession = async (message: string) => {
    if (invalidatingRef.current) return;
    invalidatingRef.current = true;
    setSessionInvalidMessage(message);
    presentationDemoActiveRef.current = false;
    setIsPresentationDemo(false);
    setSession(null);
    setUser(null);
    setProfile(null);
    setProfileStatus('idle');
    setBillingIdentity(null);
    clearSessionAppState();
    try {
      await withTimeout(supabase.auth.signOut(), 15000, 'Sign out');
    } catch {
      clearLocalAuthSession();
    }
    invalidatingRef.current = false;
  };

  const validateLiveSession = async (): Promise<boolean> => {
    // Prevent concurrent validation runs
    if (validatingRef.current) return true;
    if (presentationDemoActiveRef.current || isPresentationDemo) return true;

    validatingRef.current = true;
    try {
      const {
        data: { user: liveUser },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError) {
        // Transient / recoverable errors — do NOT terminate the session
        if (isLockConflictError(userError) || isRateLimitError(userError)) {
          return true;
        }
        // Definitively invalid session — terminate
        if (
          isInvalidSessionError(userError) ||
          isRefreshTokenInvalidError(userError) ||
          isUserNotFoundError(userError)
        ) {
          await endInvalidSession(
            'Your session is no longer valid. Please sign in again to access your vault.'
          );
          return false;
        }
        // Unknown / network error — be conservative, do NOT log the user out
        return true;
      }

      // No error and no user means the session cleanly expired (refresh token exhausted)
      if (!liveUser) {
        if (user) {
          await endInvalidSession(
            'Your session has ended. Please sign in again to protect your private vault records.'
          );
        }
        return false;
      }

      setUser(liveUser);
      return true;
    } finally {
      validatingRef.current = false;
    }
  };

  const hydrateBillingIdentity = async (userId: string): Promise<BillingIdentityLink | null> => {
    let row: BillingIdentityLink | null = null;

    const direct = await loadBillingIdentityForUser(supabase, userId);
    row = direct.row;

    if (!row && direct.error) {
      console.warn('[ErtzyxPlan] billing_identity_links client query failed', {
        userId,
        error: direct.error,
      });
      const api = await fetchBillingIdentityViaApi();
      if (api.row && api.userId && api.userId !== userId) {
        console.warn('[ErtzyxPlan] plan-context returned billing row for different user', {
          authUserId: userId,
          apiUserId: api.userId,
        });
        row = null;
      } else {
        row = api.row;
      }
      if (api.error) {
        console.warn('[ErtzyxPlan] billing plan-context API error', api.error);
      }
    }

    const scopedRow = row && row.user_id === userId ? row : null;
    if (row && row.user_id !== userId) {
      console.warn('[ErtzyxPlan] Ignoring billing_identity_links row — user_id mismatch', {
        authUserId: userId,
        rowUserId: row.user_id,
      });
    }
    setBillingIdentity(scopedRow);

    const tier = resolvePlanTierFromBillingIdentity(scopedRow);
    const status = subscriptionStatusFromBilling(scopedRow);
    logPlanResolution({
      authUserId: userId,
      billingIdentityRow: scopedRow,
      resolvedPlan: tier,
      resolvedSubscriptionStatus: status,
      source: 'AuthContext.hydrateBillingIdentity',
    });

    return scopedRow;
  };

  const fetchProfile = async (userId: string) => {
    setProfileStatus('loading');
    try {
      const billingRow = await hydrateBillingIdentity(userId);
      const {
        data: { user: authUser },
      } = await supabase.auth.getUser();

      const [profileResult, lifecycleResult] = await Promise.all([
        supabase.from('user_profiles').select('*').eq('id', userId).maybeSingle(),
        supabase
          .from('account_lifecycle')
          .select('state,deletion_scheduled_for')
          .eq('user_id', userId)
          .maybeSingle(),
      ]);
      const { data: profileData, error: profileError } = profileResult;
      const lifecycleData = lifecycleResult.error ? null : lifecycleResult.data;

      if (profileData) {
        const tier = resolvePlanTier(billingRow, profileData.subscription_tier, profileData);
        const hydratedProfile = attachLifecycleToProfile(
          { ...(profileData as UserProfile), subscription_tier: tier },
          lifecycleData
        );
        setProfile(hydratedProfile);
        setProfileStatus('ready');
        if (authUser && hydratedProfile.account_lifecycle_state === 'active') {
          try {
            await syncProfileNameFromUser(supabase, authUser);
            const { data: refreshed } = await supabase
              .from('user_profiles')
              .select('*')
              .eq('id', userId)
              .maybeSingle();
            if (refreshed) {
              const refreshedTier = resolvePlanTier(
                billingRow,
                refreshed.subscription_tier,
                refreshed
              );
              setProfile(
                attachLifecycleToProfile(
                  { ...(refreshed as UserProfile), subscription_tier: refreshedTier },
                  lifecycleData
                )
              );
            }
          } catch {
            /* sync optional */
          }
        }
        return;
      }

      if (profileError) {
        // A real query failure -- not a confirmed "no row exists". Do not synthesize a
        // "new user, onboarding incomplete" profile for what might be an existing,
        // already-onboarded user hitting a transient hiccup right after login: that's
        // exactly the shape of profile OnboardingModal must never treat as resolved. Leave
        // `profile` untouched and status 'error' -- the next auth event / debounced retry
        // (or an explicit refreshProfile()) gets another chance to resolve correctly.
        console.warn('[ErtzyxPlan] user_profiles fetch failed', profileError.message);
        setProfileStatus('error');
        return;
      }

      // No error and no row: a confirmed new user with no profile row yet.
      if (authUser) {
        setProfile(buildSyntheticProfile(authUser, billingRow));
        setProfileStatus('ready');
      } else {
        setProfileStatus('error');
      }
    } catch (err) {
      // Same reasoning as the profileError branch above: an exception means we don't
      // actually know this user's real state, so never synthesize a profile here either.
      console.warn('[ErtzyxPlan] fetchProfile failed', err);
      setProfileStatus('error');
    }
  };

  const debouncedFetchProfile = (userId: string) => {
    if (profileFetchTimerRef.current) {
      clearTimeout(profileFetchTimerRef.current);
    }
    profileFetchTimerRef.current = setTimeout(() => {
      void fetchProfile(userId);
    }, 200);
  };

  useEffect(() => {
    // Get initial session (or opt-in presentation demo cookie)
    const initSession = async () => {
      try {
        const {
          data: { session: currentSession },
        } = await supabase.auth.getSession();
        if (currentSession?.user) {
          presentationDemoActiveRef.current = false;
          setIsPresentationDemo(false);
          setSession(currentSession);
          setUser(currentSession.user);
          // Don't call validateLiveSession() here — getSession() already validated the token,
          // and onAuthStateChange fires immediately after with the INITIAL_SESSION event.
          // The 45-second poll + focus handler will catch any subsequent expiry.
          debouncedFetchProfile(currentSession.user.id);
          return;
        }

        if (isPresentationDemoFeatureEnabled()) {
          const res = await fetch('/api/presentation-demo', { credentials: 'same-origin' });
          if (res.ok) {
            const body = (await res.json()) as { active?: boolean; role?: string };
            if (body.active && (body.role === 'individual' || body.role === 'institution')) {
              presentationDemoActiveRef.current = true;
              setIsPresentationDemo(true);
              setUser(buildPresentationDemoUser(body.role));
              setSession(null);
              setProfile(buildPresentationDemoProfile(body.role) as UserProfile);
              setProfileStatus('ready');
              return;
            }
          }
        }
      } catch {
        // ignore init errors
      } finally {
        setLoading(false);
      }
    };

    void initSession();

    // Listen for auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, currentSession) => {
      if (event === 'SIGNED_OUT') {
        presentationDemoActiveRef.current = false;
        setIsPresentationDemo(false);
        setSession(null);
        setUser(null);
        setProfile(null);
        setProfileStatus('idle');
        setBillingIdentity(null);
        setLoading(false);
        return;
      }

      if (currentSession?.user) {
        presentationDemoActiveRef.current = false;
        setIsPresentationDemo(false);
        setSession(currentSession);
        setUser((prev: { id?: string } | null) => {
          if (prev?.id && prev.id !== currentSession.user.id) {
            setBillingIdentity(null);
            setProfile(null);
            setProfileStatus('idle');
          }
          return currentSession.user;
        });
        setProfileStatus((prev) => (prev === 'ready' ? prev : 'loading'));
        debouncedFetchProfile(currentSession.user.id);
        setLoading(false);
        return;
      }

      if (presentationDemoActiveRef.current) {
        setLoading(false);
        return;
      }

      if (event === 'INITIAL_SESSION') {
        setLoading(false);
        return;
      }

      setSession(null);
      setUser(null);
      setProfile(null);
      setIsPresentationDemo(false);
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
      if (profileFetchTimerRef.current) {
        clearTimeout(profileFetchTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-validate JWT with Supabase while dashboard is open (e.g. user deleted in Auth admin)
  useEffect(() => {
    if (!user?.id || isPresentationDemo || sessionInvalidMessage) return;

    const runCheck = () => {
      void validateLiveSession();
    };

    runCheck();
    const interval = window.setInterval(runCheck, 45_000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') runCheck();
    };
    window.addEventListener('focus', runCheck);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', runCheck);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isPresentationDemo, sessionInvalidMessage]);

  // Auto-refresh when profile tier or billing identity changes (e.g. Stripe webhook / admin link)
  useEffect(() => {
    if (!user?.id || isPresentationDemo) return;
    const channel = supabase
      .channel(`billing-plan-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_profiles',
          filter: `id=eq.${user.id}`,
        },
        (payload) => {
          if (
            payload.new &&
            (payload.new as { subscription_tier?: string }).subscription_tier !== undefined
          ) {
            void fetchProfile(user.id);
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'billing_identity_links',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          void hydrateBillingIdentity(user.id);
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'account_lifecycle',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          void fetchProfile(user.id);
        }
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isPresentationDemo]);

  const signUp = async (email: string, password: string, metadata: Record<string, any> = {}) => {
    const normalizedEmail = normalizeEmail(email);
    const { data, error } = await withTimeout(
      supabase.auth.signUp({
        email: normalizedEmail,
        password,
        options: {
          data: {
            full_name: metadata?.fullName || metadata?.full_name || '',
            avatar_url: metadata?.avatarUrl || metadata?.avatar_url || '',
            role: metadata?.role || 'individual',
            institution_name: metadata?.institutionName || metadata?.institution_name || '',
            org_type: metadata?.org_type || '',
            institution_type: metadata?.institution_type || '',
            location: metadata?.location || '',
          },
          emailRedirectTo: (() => {
            const origin =
              typeof window !== 'undefined' ? window.location.origin : 'https://ledger.ertzyx.com';
            const redirect = new URL('/auth/confirm', origin);
            redirect.searchParams.set('type', 'signup');
            redirect.searchParams.set('email', normalizedEmail);
            return redirect.toString();
          })(),
        },
      }),
      15000,
      'Signup'
    );
    if (error) throw new Error(mapSignupError(error));
    assertNewSignUpUser(data);
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await withTimeout(
      supabase.auth.signInWithPassword({
        email: normalizeEmail(email),
        password,
      }),
      15000,
      'Login'
    );
    if (error) throw new Error(mapLoginError(error));
    return data;
  };

  const signOut = async (options?: { redirectToLogin?: boolean; sessionMessage?: string }) => {
    setSessionInvalidMessage(null);
    // Must run before the session below is torn down -- disabling the
    // subscription server-side requires the still-valid auth cookie.
    await unsubscribeCurrentDevicePush();
    void fetch('/api/presentation-demo', { method: 'DELETE', credentials: 'same-origin' }).catch(
      () => {
        // ignore
      }
    );
    presentationDemoActiveRef.current = false;
    setIsPresentationDemo(false);
    setSession(null);
    setUser(null);
    setProfile(null);
    setProfileStatus('idle');
    setBillingIdentity(null);
    clearSessionAppState();
    try {
      await withTimeout(supabase.auth.signOut(), 15000, 'Sign out');
    } catch {
      clearLocalAuthSession();
    }
    if (options?.sessionMessage) {
      // Show the session-ended overlay with a custom message instead of redirecting
      setSessionInvalidMessage(options.sessionMessage);
    } else if (options?.redirectToLogin !== false && typeof window !== 'undefined') {
      window.location.href = '/login';
    }
  };

  const getCurrentUser = async () => {
    const {
      data: { user: currentUser },
    } = await supabase.auth.getUser();
    return currentUser;
  };

  const isEmailVerified = () => {
    return !!user?.email_confirmed_at;
  };

  const getUserProfile = async (userId?: string): Promise<UserProfile | null> => {
    const targetId = userId || user?.id;
    if (!targetId) return null;
    if (isPresentationDemo && isPresentationDemoUserId(targetId) && profile) {
      return profile;
    }
    try {
      const [profileResult, lifecycleResult] = await Promise.all([
        supabase.from('user_profiles').select('*').eq('id', targetId).single(),
        targetId === user?.id
          ? supabase
              .from('account_lifecycle')
              .select('state,deletion_scheduled_for')
              .eq('user_id', targetId)
              .maybeSingle()
          : Promise.resolve({ data: null, error: null }),
      ]);
      if (profileResult.error) return null;
      return attachLifecycleToProfile(
        profileResult.data as UserProfile,
        lifecycleResult.error ? null : lifecycleResult.data
      );
    } catch {
      return null;
    }
  };

  const refreshProfile = async () => {
    if (isPresentationDemo && user) {
      const r = user.user_metadata?.role;
      const role = r === 'institution' ? 'institution' : 'individual';
      setProfile(buildPresentationDemoProfile(role) as UserProfile);
      setProfileStatus('ready');
      return;
    }
    if (user?.id) {
      await fetchProfile(user.id);
    }
  };

  const scopedBillingForProvider =
    billingIdentity?.user_id && user?.id && billingIdentity.user_id === user.id
      ? billingIdentity
      : null;
  const entitlement = useMemo(
    () => resolveEntitlement(profile, scopedBillingForProvider),
    [profile, scopedBillingForProvider]
  );
  const resolvedPlan = entitlement.tier;
  const resolvedSubscriptionStatus = entitlement.status;

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        profileStatus,
        billingIdentity: scopedBillingForProvider,
        resolvedPlan,
        resolvedSubscriptionStatus,
        loading,
        isPresentationDemo,
        signUp,
        signIn,
        signOut,
        getCurrentUser,
        isEmailVerified,
        getUserProfile,
        refreshProfile,
        sessionInvalidMessage,
        clearSessionInvalidMessage: () => setSessionInvalidMessage(null),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
